// React import
import React, { useState, useMemo, useCallback, useEffect }  from 'react';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';

// MUI imports
import Autocomplete from '@mui/material/Autocomplete';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import { grey, green } from '@mui/material/colors';


// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReplyClock, faPlus, faPlay, faPause } from '@fortawesome/pro-light-svg-icons';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';

// Custom components
import Header from './Header.js';

function CampaignsMain(props) {
    const { organization, featureToggle } = useOutletContext();

    const navigate = useNavigate();
    const location = useLocation();

    const [campaigns, setCampaigns] = useState(FAKE_CAMPAIGNS);

    const filterStorage = sessionStorage.getItem(`CampaignsMain.filter`);
    const [ filter, setFilter ] = useState(filterStorage ? JSON.parse(filterStorage) : {
        tags: [], 
        names: [], 
        owners: [], 
        freetext: [], 
        advanced: { 
            dateCreated: [],
            lastActivity: [],
            sources: [], 
            destinations: [], 
            potentialProfit: [ 0, 1000000 ], 
            actualProfit: [ 0, 1000000 ] 
        }
    });

    function updateCampaign({ campaign, update }) {
        setCampaigns(campaigns.map(c => c.id === campaign.id ? { ...c, ...update } : c));
    }

    function onOpen({ campaign, newWindow }) {

        if (newWindow) {
            if (!location.pathname || location.pathname.length < 2) {
                window.open(`https://app.monada.ai/organization/${organization.alias}/campaigns/${campaign.id.toLowerCase()}`, '_blank');
            } else {
                window.open(`${location.pathname}/${campaign.id.toLowerCase()}?${location.search}`, '_blank');
            }
            return;
        }

        navigate(`./${encodeURIComponent(campaign.id.toLowerCase())}`);
    }

    function onNewCampaign() {
        onOpen({ campaign: { id: 'new' } });
    }

    const searchOptions = useMemo(() => {
        const tags = _(campaigns)
            .map(a => a.tags)
            .flatten()
            .uniqBy(a => a?.label)
            .compact()
            .sortBy(a => a?.label)
            .value();
        const owners = _(campaigns)
            .map(a => a.owner)
            .sort()
            .uniq()
            .compact()
            .value();
        const names = _(campaigns)
            .map(a => a.name)
            .sort()
            .value();
        return [
            ...tags.map(t => ({ type: 'tag', value: t })), 
            ...names.map(n => ({ type: 'name', value: n })),
            ...owners.map(n => ({ type: 'owner', value: n })),
        ]
    }, [campaigns]);

    const filteredCampaigns = useMemo(() => {
        const filtered = (campaigns || []).filter(campaign => {
            let toFilter = true;

            filter.tags.forEach(tag => { if (!campaign.tags?.find(_tag => _.isEqual(tag, _tag))) toFilter = false; });
            filter.names.forEach(name => { if (campaign.name.indexOf(name) === -1) toFilter = false; });
            filter.owners.forEach(name => { if ((campaign.owner || '').indexOf(name) === -1) toFilter = false; });
            if (_.some(filter.freetext, freetext => JSON.stringify(campaign).toLowerCase().indexOf(freetext.toLowerCase()) === -1)) toFilter = false;

            return toFilter;
        });

        return filtered;
    }, [campaigns, filter]);

    if (!featureToggle['feature.campaigns-and-audiences']) return null;

    return (
        <Box sx={{ paddingBottom: '36px', background: '#fafbfc' }}>
            <Header
                icon={faReplyClock}
                title={'Campaigns'}
                buttons={[{
                    title: 'New Campaign',
                    icon: faPlus,
                    color: 'monada-button',
                    action: onNewCampaign,
                }]}
            />
            <Box style={{ padding: '12px', paddingTop: '24px' }}>
                <Box>
                    <Autocomplete
                        value={[
                            ...filter.tags.map(t => ({ type: 'tag', value: t })),
                            ...filter.names.map(t => ({ type: 'name', value: t })),
                            ...filter.owners.map(t => ({ type: 'owner', value: t })),
                            ...filter.freetext.map(t => ({ type: 'freetext', value: t }))
                        ]}
                        options={searchOptions}
                        filterOptions={(options, state) => {
                            const { inputValue } = state;
                            const type = inputValue.startsWith('#') ? ['tag'] : inputValue.startsWith('@') ? ['name', 'owner'] : null;
                            if (!type) return [];
                            const value = inputValue.substr(1);
                            if (value.length === 0) return [];
                            return options.filter(o => type.includes(o.type) && (o.value.label || o.value).toLowerCase().startsWith(value.toLowerCase()));
                        }}
                        getOptionLabel={option => {
                            if (typeof option === 'string') return option;
                            if (typeof option.value === 'string') return option.value;
                            if (typeof option.value?.label === 'string') return option.value.label;
                            return '';
                        }}
                        freeSolo
                        multiple
                        onChange={(e, value) => {
                            const tags = [
                                ...value.filter(v => v.type === 'tag').map(t => t.value),
                                ...value.filter(v => typeof(v) === 'string' && v.startsWith('#')).map(t => ({ label: t.substr(1) }))
                            ];
                            const names = [
                                ...value.filter(v => v.type === 'name').map(t => t.value),
                                ...value.filter(v => typeof(v) === 'string' && v.startsWith('@')).map(t => t.substr(1))
                            ];
                            const owners = [
                                ...value.filter(v => v.type === 'owner').map(t => t.value),
                            ];
                            const freetext = [
                                ...value.filter(v => v.type === 'freetext').map(t => t.value),
                                ...value.filter(v => typeof (v) === 'string' && !v.startsWith('#') && !v.startsWith('@')).map(t => t)
                            ];
                            setFilter(f => ({ ...f, tags, names, owners, freetext }));
                        }}
                        renderTags={(value, props) => (_.compact([
                            ...filter.tags.map((t, i) => <Chip label={'Tag: ' + t.label} {...props({ index: i })} />),
                            ...filter.names.map((n, i) => <Chip label={'Name: ' + n} {...props({ index: filter.tags.length + i })} />),
                            ...filter.owners.map((n, i) => <Chip label={'Team member: ' + n} {...props({ index: filter.tags.length + filter.names.length + i })} />),
                            ...filter.freetext.map((n, i) => <Chip label={'Free text: ' + n} {...props({ index: filter.tags.length + filter.names.length + filter.owners.length + i })} />)
                        ]))}
                        renderInput={(params) => <TextField placeholder='Search... (Use @ to search in title, # for hash, or just enter text for free text search)' {...params} />}
                    />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', marginTop: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flex: 1 }}>
                        <Typography variant='caption' component={Box} sx={{ opacity: 0.5 }}>
                            Showing {filteredCampaigns.length === 1 ? 'one result' : `${filteredCampaigns.length} results`}
                        </Typography>
                    </Box>
                </Box>
                <CampaignsMainList campaigns={filteredCampaigns} onOpen={onOpen} updateCampaign={updateCampaign} />
            </Box>
      </Box>
  );
}

function CampaignsMainList(props) {
    const { campaigns, onOpen, updateCampaign } = props;

    const [pageNumber, setPageNumber] = useState(1);

    const handleScroll = useCallback(() => {
        const bottom =
            window.innerHeight + window.scrollY >= document.body.offsetHeight - 20;
        if (bottom) {
            setPageNumber(prevPageNumber => {
                if (prevPageNumber * 20 > campaigns.length) return prevPageNumber;
                return prevPageNumber + 1
            });
        }
    }, [ campaigns.length ]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [ campaigns, handleScroll ]);

    return campaigns.map((e, i) => i < pageNumber * 20 ? renderRow({ campaign: e, onOpen, updateCampaign: (v) => updateCampaign({ campaign: e, update: v}) }) : null);
}

function renderRow({ campaign, onOpen, updateCampaign }) {
    return (
        <Paper key={campaign.id} sx={{ marginTop: '24px', border: `1px solid ${grey[300]}`}} elevation={0} >
            <Box sx={{ display: 'flex', padding: '6px', alignItems: 'center', borderBottom: `1px solid ${grey[300]}` }}>
                <Box sx={{ width: '120px'}}>
                    <Typography
                        variant='caption'
                        sx={{ padding: '6px 12px', backgroundColor: campaign.state === 'active' ? green[500] : grey[500], borderRadius: '12px', color: 'white' }}>
                        {campaign.state === 'active' ? 'Active' : 'Paused'}
                    </Typography>
                </Box>
                <Box>
                    <Button variant='text' color='primary' sx={{ color: '#2185D0', textDecoration: 'underline' }} onClick={() => onOpen({ campaign })}>
                        <Typography variant='h6' sx={{ textDecoration: 'none', fontWeight: 700 }}>
                            {campaign.name}
                        </Typography>
                    </Button>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Box sx={{ borderLeft: `1px solid ${grey[300]}`}} >
                    <Button sx={{ display: 'flex', alignItems: 'center', textTransform: 'none', color: grey[900] }} onClick={() => updateCampaign({ state: campaign.state === 'active' ? 'pause' : 'active' })}>
                        {campaign.state === 'active' ? <FontAwesomeIcon icon={faPause} /> : <FontAwesomeIcon icon={faPlay} />}
                        &nbsp; &nbsp;
                        <Typography variant='caption'>
                            {campaign.state === 'active' ? 'Pause' : 'Run'}
                        </Typography>
                    </Button>
                </Box>
            </Box>
            <Box sx={{ padding: '6px', display: 'flex', fontWeight: 500, borderBottom: `1px solid ${grey[300]}` }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <div style={{ display: 'flex', padding: '6px' }}>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                Created <b>{campaign.created ? dayjs.duration((campaign.created - new Date().getTime())/1000, 'seconds').humanize(true) : 'n/a'}</b>
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                &nbsp;|&nbsp;
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                Companies <b>{campaign.accountIds.length}</b>
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                &nbsp;|&nbsp;
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                Opened <b>{campaign.accountsState.opened.length} ({Math.round(campaign.accountsState.opened.length * 100 / campaign.accountIds.length)}%)</b>
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                &nbsp;|&nbsp;
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                Finished <b>{campaign.accountsState.opened.length} ({Math.round(campaign.accountsState.finished.length * 100 / campaign.accountIds.length)}%)</b>
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                &nbsp;|&nbsp;
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                Created by <b>{campaign.owner ? campaign.owner : 'n/a'}</b>.
                            </Typography>
                        </div>
                    </div>
                </Box>
                <Button variant='text' onClick={() => onOpen({ campaign, newWindow: true })} sx={{ marginTop: '6px', height: '24px', textTransform: 'none' }}>
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    &nbsp;
                    Open in new window
                </Button>
            </Box>
            {(campaign.tags || []).filter(t => !t.label.startsWith('Pipeline: ')).length > 0 && (
                <Box sx={{ padding: '6px' }} >
                    {campaign.tags.filter(t => !t.label.startsWith('Pipeline: ')).map(tag => (<Chip sx={{ marginRight: '6px' }} label={tag.label} key={tag.label} />))}
                </Box>
            )}
        </Paper>
    );
}

const FAKE_ACCOUNTS = Array.from({length: 29}, () => uuidv4());

const FAKE_CAMPAIGNS = [{
    id: 'bc3eacb7-b001-4c33-aea2-40b1a0d9c013',
    name: 'Welcome',
    accountIds: FAKE_ACCOUNTS,
    state: 'active',
    accountsState: {
        'finished': FAKE_ACCOUNTS.slice(0, 5),
        'opened': FAKE_ACCOUNTS.slice(5, 17),
    },
    tags: [{ label: 'Important' }],
    created: 1711832400000,
    owner: 'admin@monada.ai'
}, {
    id: '60784be6-17cd-4a66-91d2-8382e86975c8',
    name: 'Follow ups',
    accountIds: FAKE_ACCOUNTS,
    state: 'paused',
    accountsState: {
        'finished': FAKE_ACCOUNTS.slice(0, 13),
        'opened': FAKE_ACCOUNTS.slice(13, 24),
    },
    created: 1701622400000,
    owner: 'admin@monada.ai'
}];

export default CampaignsMain
